import { useQuery } from '@tanstack/react-query'
import { reportingService } from '@/services/reportingService'
import { useApiReady } from '@/hooks/useApiReady'
import {
  DEFAULT_ESN_SORT,
  esnSortConfigKey,
  type EsnSortConfig,
} from '@/lib/reporting/esnBilledDaysSort'
import {
  reportingPeriodKey,
  type ReportingPeriod,
} from '@/lib/reporting/reportingPeriod'

// Query keys
export const dashboardDataKeys = {
  all: ['dashboardData'] as const,
  detail: (
    periodKey: string,
    teamId?: string | null,
    esnFreelancerSortKey?: string,
    esnClientSortKey?: string
  ) =>
    [
      ...dashboardDataKeys.all,
      'detail',
      periodKey,
      teamId,
      esnFreelancerSortKey,
      esnClientSortKey,
    ] as const,
}

export interface DashboardDataResponse {
  activeMissions: number
  missionsChangePercent: number
  costPerTagChartData: Array<{ name: string; value: number; fill: string }>
  costPerTeamUnitChartData: Array<{ name: string; value: number; fill: string }>
  costPerProjectCodeChartData: Array<{ name: string; value: number; fill: string }>
  missingReports: Array<{
    id: string
    freelancerId: string
    freelancerName: string
    mission: string
    missionId: string
    lastReminderSentAt: string | null
    referentName: string | null
  }>
  accountingSummary: {
    paid: number
    pending: number
    notValidated: number
    total: number
    costChangePercent: number
    paidChangePercent: number
    chartData: Array<{ name: string; value: number; fill: string; status: string }>
  }
  recentPayments: {
    payments: Array<{
      id: string
      activityReportId: string | null
      status: string
      amountExclVAT: number
      currency: string
      paidAt: string | null
      invoiceVerificationStatus: string | null
      activityReport: {
        id: string
        reportMonth: string
        freelancer: { id: string; name: string } | null
        mission: { id: string; name: string } | null
      } | null
    }>
    total: number
  }
  reminderDatesByFreelancerId: Record<string, string>
  esnBilledDays: {
    total: number
    totalAmountExclVat: number
    totalClientAmountExclVat: number
    totalMargin: number
    byFreelancer: Array<{
      freelancerId: string
      name: string
      days: number
      amountExclVat: number
      clientAmountExclVat: number
      margin: number
    }>
    byClient: Array<{
      clientId: string | null
      name: string | null
      days: number
      amountExclVat: number
      clientAmountExclVat: number
      margin: number
    }>
  } | null
}

/**
 * Hook pour récupérer les données du dashboard reporting via un seul endpoint unifié.
 * Toutes les données sont calculées côté serveur.
 */
export function useDashboardData(
  period: ReportingPeriod,
  teamId?: string | null,
  options?: {
    enabled?: boolean
    esnFreelancerSort?: EsnSortConfig
    esnClientSort?: EsnSortConfig
  }
) {
  const apiReady = useApiReady()
  const periodKey = reportingPeriodKey(period)
  const esnFreelancerSort = options?.esnFreelancerSort ?? DEFAULT_ESN_SORT
  const esnClientSort = options?.esnClientSort ?? DEFAULT_ESN_SORT

  return useQuery({
    queryKey: dashboardDataKeys.detail(
      periodKey,
      teamId,
      esnSortConfigKey(esnFreelancerSort),
      esnSortConfigKey(esnClientSort)
    ),
    queryFn: async (): Promise<DashboardDataResponse> => {
      const response = await reportingService.getDashboard(period, teamId, {
        freelancer: esnFreelancerSort,
        client: esnClientSort,
      })

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch dashboard data')
      }

      return response.data as DashboardDataResponse
    },
    enabled: apiReady && options?.enabled !== false && !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes (reporting data changes infrequently)
  })
}
